import React, { useState, useRef, useEffect } from 'react';
import { Download, Image, FileSpreadsheet, Loader2 } from 'lucide-react';

/**
 * ExportButton Component
 * A reusable export button that shows PNG and/or CSV download options.
 * Only renders if the user's role is allowed to export.
 *
 * @param {Object} props
 * @param {Function} [props.onExportPNG] - Handler for PNG export
 * @param {Function} [props.onExportCSV] - Handler for CSV export
 * @param {string} [props.userRole] - Current user role (only superadmin/stakeholder see this)
 * @param {string} [props.className] - Additional CSS classes
 * @param {string} [props.size] - 'sm' | 'md' (default: 'sm')
 * @param {string} [props.position] - 'top-right' | 'inline' (default: 'top-right')
 */
const ExportButton = ({
  onExportPNG,
  onExportCSV,
  userRole,
  className = '',
  size = 'sm',
  position = 'top-right',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click — must be above any conditional return
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  // Don't render if role not allowed
  if (!userRole || !['superadmin', 'stakeholder'].includes(userRole)) {
    return null;
  }

  const hasMultipleOptions = onExportPNG && onExportCSV;
  const singleAction = !hasMultipleOptions ? (onExportPNG || onExportCSV) : null;
  const singleFormat = !hasMultipleOptions ? (onExportPNG ? 'PNG' : 'CSV') : null;

  const handleClick = async () => {
    if (hasMultipleOptions) {
      setIsOpen(prev => !prev);
    } else if (singleAction) {
      setIsExporting(true);
      try {
        await singleAction();
      } finally {
        setIsExporting(false);
      }
    }
  };

  const handleExport = async (exportFn) => {
    setIsOpen(false);
    setIsExporting(true);
    try {
      await exportFn();
    } finally {
      setIsExporting(false);
    }
  };

  const sizeClasses = size === 'sm'
    ? 'w-7 h-7 text-xs'
    : 'w-8 h-8 text-sm';

  const iconSize = size === 'sm' ? 14 : 16;

  const positionClasses = position === 'top-right'
    ? 'absolute top-2 right-2 z-20'
    : 'relative inline-flex';

  return (
    <div ref={dropdownRef} className={`${positionClasses} ${className}`} data-export-btn="true">
      <button
        onClick={handleClick}
        disabled={isExporting}
        className={`${sizeClasses} flex items-center justify-center rounded-lg border border-slate-200 bg-white/90 backdrop-blur-sm text-slate-500 hover:text-slate-700 hover:border-slate-300 hover:bg-white shadow-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed`}
        title={hasMultipleOptions ? 'Export' : `Export as ${singleFormat}`}
        aria-label={hasMultipleOptions ? 'Export options' : `Export as ${singleFormat}`}
      >
        {isExporting ? (
          <Loader2 size={iconSize} className="animate-spin" />
        ) : (
          <Download size={iconSize} />
        )}
      </button>

      {/* Dropdown for multiple options */}
      {isOpen && hasMultipleOptions && (
        <div className="absolute top-full right-0 mt-1 bg-white rounded-lg shadow-xl border border-slate-200 py-1 min-w-[140px] z-50 animate-in fade-in slide-in-from-top-1 duration-150">
          {onExportPNG && (
            <button
              onClick={() => handleExport(onExportPNG)}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <Image size={14} className="text-blue-500" />
              <span>Export as PNG</span>
            </button>
          )}
          {onExportCSV && (
            <button
              onClick={() => handleExport(onExportCSV)}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <FileSpreadsheet size={14} className="text-green-500" />
              <span>Export as CSV</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ExportButton;
